import store from "@/redux/store";
import { api } from "./api";
import {
  classifyConversation, getConversationId, getPayloadChatId, getPayloadSenderId,
  isKnownCustomerCareConversation, readMarketplaceConversation,
  type MarketplaceConversation,
} from "./conversationBoundary";

const API_CHAT_BASE_URL = "https://ilosiwaju-mbaay-2025.com/api/v1/chat";
const API_VENDOR_BASE_URL = "https://ilosiwaju-mbaay-2025.com/api/v1/vendor";
type Data = Record<string, unknown>;
const object = (value: unknown): Data => value && typeof value === "object" && !Array.isArray(value) ? value as Data : {};
let registryAccount = "";
let registrySequence = 0;
const conversations = new Map<string, MarketplaceConversation>();

// Customer marketplace requests must never select a persisted vendor session.
export const getAuthToken = () => store.getState().user.token || null;
const account = () => {
  const state = store.getState().user;
  if (!state.user?._id || !state.token) throw new Error("Please sign in to use marketplace messages.");
  if (registryAccount !== state.user._id) { registryAccount = state.user._id; registrySequence += 1; conversations.clear(); }
  return { role: "user" as const, id: state.user._id };
};
const assertAccount = (id: string) => {
  if (account().id !== id) throw new Error("Your account changed. Please reopen messages.");
};
const responseData = (value: unknown): Data => {
  const data = object(value);
  if (data.success === false) throw new Error(typeof data.message === "string" ? data.message : "The chat request failed.");
  return data;
};

const fetchUserChats = async (current: ReturnType<typeof account>, sequence: number) => {
  const response = await api.get(`${API_CHAT_BASE_URL}/chats`);
  assertAccount(current.id);
  const data = responseData(response.data);
  if (!Array.isArray(data.chats)) throw new Error("The conversation response is missing its chat list.");
  if (sequence !== registrySequence) return { ...data, chats: [...conversations.values()].filter(chat => readMarketplaceConversation(chat.raw, current)).map(chat => chat.raw), rejectedConversations: [], stale: true };
  const accepted: Data[] = [];
  const rejectedConversations: { id: string | null; reason: string }[] = [];
  const seen = new Set<string>();
  // Validate before conversation data enters inbox state or socket rooms.
  for (const raw of data.chats) {
    const chat = readMarketplaceConversation(raw, current);
    if (!chat) {
      rejectedConversations.push({ id: getConversationId(raw), reason: classifyConversation(raw, current) === "customer_care" ? "customer_care" : "invalid_marketplace_participants" });
      continue;
    }
    if (seen.has(chat._id)) continue;
    seen.add(chat._id); conversations.set(chat._id, chat); accepted.push(chat.raw);
  }
  for (const id of conversations.keys()) if (!seen.has(id)) conversations.delete(id);
  return { ...data, chats: accepted, rejectedConversations, stale: false };
};

let pendingList: { accountId: string; sequence: number; promise: ReturnType<typeof fetchUserChats> } | null = null;
export const getUserChats = async () => {
  const current = account();
  if (pendingList?.accountId === current.id && pendingList.sequence === registrySequence) return pendingList.promise;
  const sequence = ++registrySequence;
  const promise = fetchUserChats(current, sequence);
  const request = { accountId: current.id, sequence, promise };
  pendingList = request;
  try { return await promise; }
  finally { if (pendingList === request) pendingList = null; }
};

const verifiedConversation = async (chatId: string) => {
  const current = account();
  if (!chatId || isKnownCustomerCareConversation(current, chatId)) throw new Error("Customer Care uses its own support conversation.");
  if (!conversations.has(chatId)) await getUserChats();
  assertAccount(current.id);
  const chat = conversations.get(chatId);
  if (!chat || !readMarketplaceConversation(chat.raw, current)) throw new Error("This conversation is not a verified customer and vendor chat.");
  return { current, chat };
};
export const isVerifiedUserChat = (chatId: string) => {
  try { const current = account(); const chat = conversations.get(chatId); return !!chat && !!readMarketplaceConversation(chat.raw, current); }
  catch { return false; }
};
export const acceptUserChatMessage = (chatId: string, message: unknown) => {
  if (!isVerifiedUserChat(chatId) || object(message).isCustomerCareChat === true) return false;
  const chat = conversations.get(chatId)!;
  const room = getPayloadChatId(message); const sender = getPayloadSenderId(message);
  return (!room || room === chatId) && !!sender && chat.participants.some(participant => participant.id === sender);
};
export const acceptUserSocketMessage = (message: unknown) => {
  const chatId = getPayloadChatId(message);
  return !!chatId && acceptUserChatMessage(chatId, message);
};
export const registerUserChat = (raw: unknown) => {
  const chat = readMarketplaceConversation(raw, account());
  if (chat) { registrySequence += 1; conversations.set(chat._id, chat); }
  return chat;
};

export const startChat = async (receiverId: string) => {
  const current = account();
  if (!receiverId || receiverId === current.id) throw new Error("Choose a vendor to start a conversation.");
  const response = await api.post(`${API_CHAT_BASE_URL}/create_or_get_chat`, { receiverId });
  assertAccount(current.id);
  const data = responseData(response.data); const id = getConversationId(data.chat);
  if (!id) throw new Error("The server did not return a conversation identifier.");
  // ID-only create responses must be hydrated before history/sending is allowed.
  if (!registerUserChat(data.chat)) await getUserChats();
  const { chat } = await verifiedConversation(id);
  if (chat.counterpart.id !== receiverId) throw new Error("The conversation does not belong to the selected vendor.");
  return { ...data, success: true, chat: { ...chat.raw, _id: chat._id } };
};
export const getChatMessages = async (chatId: string) => {
  const { current } = await verifiedConversation(chatId);
  const response = await api.get(`${API_CHAT_BASE_URL}/chat/${chatId}/messages`);
  assertAccount(current.id);
  const data = responseData(response.data);
  if (!Array.isArray(data.messages)) throw new Error("The message response is missing its history.");
  const messages = data.messages.filter(message => acceptUserChatMessage(chatId, message));
  return { ...data, messages, rejectedMessages: data.messages.length - messages.length };
};
export const sendMessage = async (chatId: string, content: string, replyTo?: string) => {
  const { current } = await verifiedConversation(chatId);
  if (!content.trim()) throw new Error("Enter a message first.");
  const response = await api.post(`${API_CHAT_BASE_URL}/chat/${chatId}/message`, { chatId, content: content.trim(), replyTo });
  assertAccount(current.id); return responseData(response.data);
};
export const sendMediaMessage = async (chatId: string, files: File[], replyTo?: string, content?: string) => {
  const { current } = await verifiedConversation(chatId);
  const images = files.filter(file => file.type.startsWith("image/"));
  const videos = files.filter(file => file.type.startsWith("video/"));
  const documents = files.filter(file => !file.type.startsWith("image/") && !file.type.startsWith("video/"));
  if (!files.length || images.length > 5 || videos.length > 1 || documents.length > 5) throw new Error("Attach up to 5 photos, 1 video, and 5 documents.");
  const formData = new FormData();
  images.forEach(file => formData.append("images", file));
  videos.forEach(file => formData.append("video", file));
  documents.forEach(file => formData.append("documents", file));
  if (replyTo) formData.append("replyTo", replyTo);
  if (content?.trim()) formData.append("content", content.trim());
  const response = await api.post(`${API_CHAT_BASE_URL}/chat/${chatId}/send_media_message`, formData, { headers: { "Content-Type": undefined } });
  assertAccount(current.id); return responseData(response.data);
};
export const editMessage = async (messageId: string, content: string, chatId?: string) => {
  const current = account(); if (chatId) await verifiedConversation(chatId);
  if (!messageId || messageId.startsWith("local-")) throw new Error("Only confirmed messages can be edited.");
  const response = await api.patch(`${API_CHAT_BASE_URL}/edit/${messageId}`, { content });
  assertAccount(current.id); return responseData(response.data);
};
export const deleteMessage = async (messageId: string, chatId?: string) => {
  const current = account(); if (chatId) await verifiedConversation(chatId);
  if (!messageId || messageId.startsWith("local-")) throw new Error("Only confirmed messages can be deleted.");
  const response = await api.delete(`${API_CHAT_BASE_URL}/delete/${messageId}`);
  assertAccount(current.id); return responseData(response.data);
};
export const getAllVendor = async () => {
  const current = account(); const response = await api.get(`${API_VENDOR_BASE_URL}/get_all_vendors`);
  assertAccount(current.id); return responseData(response.data);
};
export const getUnreadChatCount = async (userId: string) => {
  const current = account();
  if (userId !== current.id) throw new Error("Unread messages belong to the signed-in customer.");
  const chats = await getUserChats();
  const response = await api.get(`${API_CHAT_BASE_URL}/get_unread_chat_count/${userId}`);
  assertAccount(current.id);
  const data = responseData(response.data);
  const items = Array.isArray(data.data) ? data.data : Array.isArray(data.chats) ? data.chats : null;
  const counts: Record<string, number> = {};
  if (items) {
    for (const item of items) {
      const entry = object(item);
      const id = getConversationId(entry.chatId) || (typeof entry.chatId === "string" ? entry.chatId : null);
      const count = entry.unreadCount ?? entry.count;
      if (id && isVerifiedUserChat(id) && typeof count === "number" && Number.isFinite(count)) counts[id] = Math.max(0, count);
    }
  } else {
    for (const chat of chats.chats) if (typeof chat.unreadCount === "number") counts[String(chat._id)] = Math.max(0, chat.unreadCount);
  }
  const countAvailable = !!items || chats.chats.every(chat => typeof chat.unreadCount === "number");
  return { count: Object.values(counts).reduce((sum, count) => sum + count, 0), chats: counts, countAvailable };
};
export const markChatAsRead = async (chatId: string, userId: string) => {
  const { current } = await verifiedConversation(chatId);
  if (userId !== current.id) throw new Error("Read state belongs to the signed-in customer.");
  const response = await api.patch(`${API_CHAT_BASE_URL}/mark_chat_as_read/${chatId}/${userId}`, {});
  assertAccount(current.id); return responseData(response.data);
};
